import { useEffect, useRef } from 'react';
import { useForm, useWatch, type Control, type UseFormReturn } from 'react-hook-form';
import type { CreateListingDTO } from 'app/types/listing';
import { localeAwareFormat } from 'app/lib/date';
import { useTranslation } from 'react-i18next';
import type { UpdateListingFormValues } from 'app/screens/shared/one-listing/components/useListing';

interface UseAutomaticMessageParams {
  createForm?: UseFormReturn<CreateListingDTO>;
  updateForm?: UseFormReturn<UpdateListingFormValues, unknown>;
  /** When provided (e.g. a persisted listing.automaticMessage), reset targets this value instead of the AI-generated default. */
  persistedMessage?: string | null;
}

export function useAutomaticMessage({ createForm, updateForm, persistedMessage }: UseAutomaticMessageParams) {
  const { t } = useTranslation('new');
  // Offer editing supplies neither request form and has no FormProvider above this hook.
  const fallbackForm = useForm<CreateListingDTO>();
  const control = (createForm?.control ?? updateForm?.control ?? fallbackForm.control) as Control<CreateListingDTO>;
  const [watchItem, watchDescription, watchStart, watchEnd, watchAutomaticMessage] = useWatch({
    control,
    name: ['item', 'description', 'start', 'end', 'automaticMessage'],
    disabled: !createForm && !updateForm,
  });

  const isManuallyEdited = useRef(false);

  const assistantMessageDefault = (() => {
    const item = watchItem;
    const description = watchDescription;
    const start = watchStart;
    const end = watchEnd;

    let message = t('chooseAutomaticMessage.defaultAutomaticMessage.greeting', { firstName: '' });

    if (item?.name) {
      if (description) {
        message += t('chooseAutomaticMessage.defaultAutomaticMessage.itemWithDescription', {
          itemName: item.name,
          description,
        });
      } else {
        message += t('chooseAutomaticMessage.defaultAutomaticMessage.itemWithoutDescription', {
          itemName: item.name,
        });
      }
    }

    if (start && end && start !== end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      message += t('chooseAutomaticMessage.defaultAutomaticMessage.dateBoth', {
        startDay: startDate.getDate(),
        startMonth: localeAwareFormat(startDate, 'MMMM'),
        endDay: endDate.getDate(),
        endMonth: localeAwareFormat(endDate, 'MMMM'),
      });
    } else if (start) {
      const startDate = new Date(start);
      message += t('chooseAutomaticMessage.defaultAutomaticMessage.dateStartOnly', {
        startDay: startDate.getDate(),
        startMonth: localeAwareFormat(startDate, 'MMMM'),
      });
    } else if (end) {
      const endDate = new Date(end);
      message += t('chooseAutomaticMessage.defaultAutomaticMessage.dateEndOnly', {
        endDay: endDate.getDate(),
        endMonth: localeAwareFormat(endDate, 'MMMM'),
      });
    }

    return message;
  })();

  useEffect(() => {
    if (isManuallyEdited.current) return;
    // When a persisted message exists, don't overwrite it with the AI-generated default.
    if (persistedMessage) return;

    if (createForm) {
      createForm.setValue('automaticMessage', assistantMessageDefault);
    } else if (updateForm) {
      updateForm.setValue('automaticMessage', assistantMessageDefault);
    }
  }, [assistantMessageDefault, createForm, updateForm, persistedMessage]);

  const onChangeAutomaticMessage = (text: string) => {
    isManuallyEdited.current = true;
    if (createForm) {
      createForm.clearErrors('automaticMessage');
      createForm.setValue('automaticMessage', text);
    } else if (updateForm) {
      updateForm.clearErrors('automaticMessage');
      updateForm.setValue('automaticMessage', text);
    }
  };

  const onResetAutomaticMessage = () => {
    isManuallyEdited.current = false;
    const resetTarget = persistedMessage ?? assistantMessageDefault;
    if (createForm) {
      createForm.clearErrors('automaticMessage');
      createForm.setValue('automaticMessage', resetTarget);
    } else if (updateForm) {
      updateForm.clearErrors('automaticMessage');
      updateForm.setValue('automaticMessage', resetTarget);
    }
  };

  return {
    assistantMessageDefault,
    onChangeAutomaticMessage,
    onResetAutomaticMessage,
    watchAutomaticMessage,
  };
}
